import { Form, Formik } from 'formik'
import gql from 'graphql-tag'
import React, { useState } from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../../api/graphql'
import useRecordSubmissionMutation from '../../../../hooks/useRecordSubmissionMutation'
import InviteMemberForm from './InviteMemberForm'
import LogoUploadField from './LogoUploadField'
import MemberCard from './MemberCard'
import OrganizationDetailReadOnly from './OrganizationDetailReadOnly'
import {
  ArrowRightLeftIcon,
  EmailIcon,
  GlobeIcon,
  LocationIcon,
  PhoneIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
} from './OrganizationIcons'
import {
  ORGANIZATION_TYPE_OPTIONS,
  StyledAddressRow,
  StyledButtonRow,
  StyledDeleteButton,
  StyledDeleteDescription,
  StyledDeleteHeader,
  StyledDeleteSection,
  StyledDeleteTitle,
  StyledDeleteWarning,
  StyledDetailContainer,
  StyledDivider,
  StyledFieldGroup,
  StyledFieldsSection,
  StyledInput,
  StyledInputSmall,
  StyledInviteButton,
  StyledLabel,
  StyledLabelWithIcon,
  StyledMemberGrid,
  StyledMembersHeader,
  StyledMembersSection,
  StyledMembersTitle,
  StyledSelect,
  StyledTransferButtonRow,
  StyledTransferCancelButton,
  StyledTransferConfirmButton,
  StyledTransferDescription,
  StyledTransferForm,
  StyledTransferHeader,
  StyledTransferNote,
  StyledTransferSection,
  StyledTransferSelect,
  StyledTransferTitle,
  StyledTransferToggleButton,
  StyledTwoColumnRow,
  StyledUpdateButton,
} from './OrganizationDetail.styles'
import type { Organization } from './OrganizationsSection'
import { OrganizationMembershipsQuery } from './OrganizationsSection'

type OrganizationDetailProps = {
  organization: Organization
  currentUserId?: string
  membershipId?: string
  isOwner: boolean
  onDeleteSuccess?: () => void
}

export const ORGANIZATION_UPDATE_MUTATION = gql`
  mutation OrganizationUpdate($input: OrganizationUpdateInput!) {
    organizationUpdate(input: $input) {
      organization {
        id
        name
        organizationType
        contactEmail
        phone
        websiteUrl
        streetAddress
        city
        state
        zip
        logoPublicId
      }
      errors {
        attribute
        message
      }
    }
  }
`

const ORGANIZATION_DELETE_MUTATION = gql`
  mutation OrganizationDelete($input: OrganizationDeleteInput!) {
    organizationDelete(input: $input) {
      success
      errors {
        attribute
        message
      }
    }
  }
`

const REMOVE_MEMBER_MUTATION = gql`
  mutation OrganizationMembershipRemove($input: OrganizationMembershipRemoveInput!) {
    organizationMembershipRemove(input: $input) {
      success
      errors {
        attribute
        message
      }
    }
  }
`

const TRANSFER_OWNERSHIP_MUTATION = gql`
  mutation OrganizationTransferOwnership($input: OrganizationTransferOwnershipInput!) {
    organizationTransferOwnership(input: $input) {
      organization {
        id
        name
      }
      errors {
        attribute
        message
      }
    }
  }
`

const OrganizationDetail: React.FC<OrganizationDetailProps> = ({
  organization,
  currentUserId,
  membershipId,
  isOwner,
  onDeleteSuccess,
}) => {
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [showTransferOwnership, setShowTransferOwnership] = useState(false)
  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState('')
  const [removeMember] = useMutation(REMOVE_MEMBER_MUTATION)
  const [transferOwnership, { loading: transferLoading }] = useMutation(TRANSFER_OWNERSHIP_MUTATION)
  const [deleteOrganization, { loading: deleteLoading }] = useMutation(ORGANIZATION_DELETE_MUTATION)

  const { handleSubmit, mutationLoading } = useRecordSubmissionMutation({
    query: ORGANIZATION_UPDATE_MUTATION,
    queryName: 'organizationUpdate',
    recordName: 'organization',
    onSuccess: () => {
      toastr.success('Organization updated!')
    },
    submitOptions: { refetchQueries: () => [{ query: OrganizationMembershipsQuery }] },
  })

  // Read-only view for non-owners
  if (!isOwner) {
    return (
      <OrganizationDetailReadOnly
        organization={organization}
        currentUserId={currentUserId}
        membershipId={membershipId}
      />
    )
  }

  const handleInviteMember = () => {
    setShowInviteForm(true)
  }

  const handleCancelInvite = () => {
    setShowInviteForm(false)
  }

  const handleInviteSuccess = () => {
    setShowInviteForm(false)
  }

  const handleTransferOwnership = async () => {
    if (!selectedNewOwnerId) {
      toastr.error('Please select a new owner')
      return
    }

    if (!window.confirm('Are you sure you want to transfer ownership? This action cannot be undone.')) {
      return
    }

    try {
      const result = await transferOwnership({
        variables: {
          input: {
            organizationId: organization.id,
            newOwnerId: selectedNewOwnerId,
          },
        },
        refetchQueries: [{ query: OrganizationMembershipsQuery }],
      })

      const response = result.data?.organizationTransferOwnership
      if (response?.errors?.length > 0) {
        toastr.error(response.errors[0].message)
        return
      }

      if (response?.organization) {
        toastr.success('Ownership transferred successfully')
        setShowTransferOwnership(false)
        setSelectedNewOwnerId('')
      }
    } catch (error) {
      toastr.error('Failed to transfer ownership')
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return
    }

    try {
      const result = await removeMember({
        variables: {
          input: { id: memberId },
        },
        refetchQueries: [{ query: OrganizationMembershipsQuery }],
      })

      const response = result.data?.organizationMembershipRemove
      if (response?.errors?.length > 0) {
        toastr.error(response.errors[0].message)
        return
      }

      if (response?.success) {
        toastr.success('Member removed')
      }
    } catch (error) {
      toastr.error('Failed to remove member')
    }
  }

  const handleDeleteOrganization = async () => {
    if (!window.confirm(`Are you sure you want to delete "${organization.name}"? This action cannot be undone and will remove all members.`)) {
      return
    }

    try {
      const result = await deleteOrganization({
        variables: {
          input: { id: organization.id },
        },
        refetchQueries: [{ query: OrganizationMembershipsQuery }],
      })

      const response = result.data?.organizationDelete
      if (response?.errors?.length > 0) {
        toastr.error(response.errors[0].message)
        return
      }

      if (response?.success) {
        toastr.success('Organization deleted')
        onDeleteSuccess?.()
      }
    } catch (error) {
      toastr.error('Failed to delete organization')
    }
  }

  // Get eligible members for ownership transfer (accepted, non-owner members with user accounts)
  const eligibleNewOwners = organization.organizationMemberships.filter(
    m => m.role !== 'owner' && !m.pending && m.user,
  )

  const initialValues = {
    name: organization.name || '',
    organizationType: organization.organizationType || '',
    contactEmail: organization.contactEmail || '',
    phone: organization.phone || '',
    websiteUrl: organization.websiteUrl || '',
    streetAddress: organization.streetAddress || '',
    city: organization.city || '',
    state: organization.state || '',
    zip: organization.zip || '',
    logoPublicId: organization.logoPublicId || '',
  }

  const handleFormSubmit = (values: typeof initialValues) => {
    const input = {
      id: organization.id,
      name: values.name,
      organizationType: values.organizationType || null,
      contactEmail: values.contactEmail || null,
      phone: values.phone || null,
      websiteUrl: values.websiteUrl || null,
      streetAddress: values.streetAddress || null,
      city: values.city || null,
      state: values.state || null,
      zip: values.zip || null,
      logoPublicId: values.logoPublicId || null,
    }
    handleSubmit(input)
  }

  // Editable form for owners
  return (
    <StyledDetailContainer>
      <Formik initialValues={initialValues} onSubmit={handleFormSubmit} enableReinitialize>
        {({ values, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <StyledFieldsSection>
              <StyledFieldGroup>
                <StyledLabel>Organization Name</StyledLabel>
                <StyledInput
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter organization name"
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <StyledLabel>Organization Type</StyledLabel>
                <StyledSelect name="organizationType" value={values.organizationType} onChange={handleChange} onBlur={handleBlur}>
                  {ORGANIZATION_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </StyledSelect>
              </StyledFieldGroup>

              <LogoUploadField currentLogoPublicId={organization.logoPublicId} organizationId={organization.id} />

              <StyledFieldGroup>
                <StyledLabelWithIcon>
                  <LocationIcon />
                  Address
                </StyledLabelWithIcon>
                <StyledInput
                  type="text"
                  name="streetAddress"
                  value={values.streetAddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Street Address"
                />
                <StyledAddressRow>
                  <StyledInputSmall
                    type="text"
                    name="city"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="City"
                  />
                  <StyledInputSmall
                    type="text"
                    name="state"
                    value={values.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="State"
                  />
                  <StyledInputSmall
                    type="text"
                    name="zip"
                    value={values.zip}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Zip"
                  />
                </StyledAddressRow>
              </StyledFieldGroup>

              <StyledTwoColumnRow>
                <StyledFieldGroup>
                  <StyledLabelWithIcon>
                    <GlobeIcon />
                    Website
                  </StyledLabelWithIcon>
                  <StyledInput
                    type="url"
                    name="websiteUrl"
                    value={values.websiteUrl}
                    onChange={handleChange}
                    onBlur={e => {
                      handleBlur(e)
                      const val = values.websiteUrl.trim()
                      if (val && !val.startsWith('http://') && !val.startsWith('https://')) {
                        setFieldValue('websiteUrl', `https://${val}`)
                      }
                    }}
                    placeholder="https://example.com"
                  />
                </StyledFieldGroup>

                <StyledFieldGroup>
                  <StyledLabelWithIcon>
                    <PhoneIcon />
                    Phone
                  </StyledLabelWithIcon>
                  <StyledInput
                    type="tel"
                    name="phone"
                    value={values.phone}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '')
                      let formatted = digits
                      if (digits.length > 3 && digits.length <= 6) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                      } else if (digits.length > 6) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
                      }
                      setFieldValue('phone', formatted)
                    }}
                    onBlur={handleBlur}
                    placeholder="(555) 555-5555"
                  />
                </StyledFieldGroup>
              </StyledTwoColumnRow>

              <StyledFieldGroup>
                <StyledLabelWithIcon>
                  <EmailIcon />
                  Contact Email
                </StyledLabelWithIcon>
                <StyledInput
                  type="email"
                  name="contactEmail"
                  value={values.contactEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="contact@example.com"
                />
              </StyledFieldGroup>
            </StyledFieldsSection>

            <StyledDivider />

            <StyledMembersSection>
              <StyledMembersHeader>
                <StyledMembersTitle>
                  <UsersIcon />
                  Organization Members
                </StyledMembersTitle>
                <StyledInviteButton type="button" onClick={handleInviteMember}>
                  <UserPlusIcon />
                  INVITE MEMBER
                </StyledInviteButton>
              </StyledMembersHeader>

              {showInviteForm && (
                <InviteMemberForm
                  organizationId={organization.id}
                  organizationName={organization.name}
                  onCancel={handleCancelInvite}
                  onSuccess={handleInviteSuccess}
                />
              )}

              <StyledMemberGrid>
                {organization.organizationMemberships.map(membership => (
                  <MemberCard
                    key={membership.id}
                    membership={membership}
                    isCurrentUser={membership.user?.id === currentUserId}
                    isOwnerViewing={isOwner}
                    onRemove={handleRemoveMember}
                  />
                ))}
              </StyledMemberGrid>
            </StyledMembersSection>

            <StyledDivider />

            <StyledTransferSection>
              <StyledTransferHeader>
                <div>
                  <StyledTransferTitle>
                    <ArrowRightLeftIcon />
                    Transfer Ownership
                  </StyledTransferTitle>
                  {eligibleNewOwners.length === 0 && !showTransferOwnership && (
                    <StyledTransferNote>Add members to transfer ownership.</StyledTransferNote>
                  )}
                </div>
                {!showTransferOwnership && (
                  <StyledTransferToggleButton
                    type="button"
                    onClick={() => setShowTransferOwnership(true)}
                    disabled={eligibleNewOwners.length === 0}
                    title={eligibleNewOwners.length === 0 ? 'Add members to transfer ownership' : ''}
                  >
                    Transfer
                  </StyledTransferToggleButton>
                )}
              </StyledTransferHeader>

              {showTransferOwnership && (
                <StyledTransferForm>
                  <StyledTransferDescription>
                    Select a member to transfer ownership to. You will remain as a member after the transfer.
                  </StyledTransferDescription>
                  <StyledTransferSelect
                    value={selectedNewOwnerId}
                    onChange={e => setSelectedNewOwnerId(e.target.value)}
                  >
                    <option value="">Select new owner...</option>
                    {eligibleNewOwners.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.displayName}{member.displayEmail ? ` (${member.displayEmail})` : ''}
                      </option>
                    ))}
                  </StyledTransferSelect>
                  <StyledTransferButtonRow>
                    <StyledTransferConfirmButton
                      type="button"
                      onClick={handleTransferOwnership}
                      disabled={transferLoading || !selectedNewOwnerId}
                    >
                      {transferLoading ? 'Transferring...' : 'Confirm Transfer'}
                    </StyledTransferConfirmButton>
                    <StyledTransferCancelButton
                      type="button"
                      onClick={() => {
                        setShowTransferOwnership(false)
                        setSelectedNewOwnerId('')
                      }}
                    >
                      Cancel
                    </StyledTransferCancelButton>
                  </StyledTransferButtonRow>
                </StyledTransferForm>
              )}
            </StyledTransferSection>

            <StyledDivider />

            <StyledDeleteSection>
              <StyledDeleteHeader>
                <div>
                  <StyledDeleteTitle>
                    <TrashIcon />
                    Delete Organization
                  </StyledDeleteTitle>
                  <StyledDeleteDescription>
                    Permanently delete this organization and remove all members.
                  </StyledDeleteDescription>
                </div>
                {!organization.hasAssociatedEvents && (
                  <StyledDeleteButton type="button" onClick={handleDeleteOrganization} disabled={deleteLoading}>
                    <TrashIcon />
                    {deleteLoading ? 'Deleting...' : 'Delete'}
                  </StyledDeleteButton>
                )}
              </StyledDeleteHeader>
              {organization.hasAssociatedEvents && (
                <StyledDeleteWarning>
                  This organization cannot be deleted because it has events associated with it.
                  Please reassign or delete those events first.
                </StyledDeleteWarning>
              )}
            </StyledDeleteSection>

            <StyledButtonRow>
              <StyledUpdateButton type="submit" disabled={mutationLoading}>
                {mutationLoading ? 'SAVING...' : 'UPDATE ORGANIZATION'}
              </StyledUpdateButton>
            </StyledButtonRow>
          </Form>
        )}
      </Formik>
    </StyledDetailContainer>
  )
}

export default OrganizationDetail
